import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTenant } from "../hooks/useTenant";
import { supabase } from "../utils/supabaseClient";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { useTheme } from "../context/ThemeContext";
import { getPageBackground, getCardBackground, getPrimaryText, getSecondaryText, getMutedText } from "../utils/themeUtils";
import { Button, Input } from "../components/ui";
import { getSettings } from "../api/SettingsAPI";
import config from "../api/config";
import { getHeaders } from "../api/apiHelpers";
import { captureError } from "../utils/sentry";
import { reportSignInFailed } from "../api/sessionDiagnostics";
import { establishSession, SIGN_IN_NOT_SAVED_MESSAGE } from "../utils/establishSession";
import { safeGetItem, safeSetItem } from "@mediatechgroup/app-version";

// Everything that turns the server's answer into a signed-in app (saving the
// token, who is signed in, where to go next) lives in utils/establishSession,
// shared with the magic-link page. Re-exported for the tests that read it here.
export { SIGN_IN_NOT_SAVED_MESSAGE };

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const storedSettings = safeGetItem("settings");

  useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();

  // Extract tenant ID dynamically from current URL
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;

  // Get the redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || "/dashboard";

  // Surfaced by the axios interceptor when an authenticated request 401s
  // (expired/invalidated session). Without this, an expired session looked
  // like a silently-broken empty app. See axiosInstance.ts handleSessionExpiry.
  useEffect(() => {
    const sessionExpired = new URLSearchParams(location.search).get("sessionExpired");
    if (sessionExpired === "1") {
      toast.info("Your session has expired. Please sign in again.");
    } else if (sessionExpired === "idle") {
      // The club's staff idle sign-out (PM aa0f43a7).
      toast.info("You were signed out because this screen was not used for a while. Please sign in again.");
    }
  }, [location.search]);

  const resendConfirmation = async () => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: {
        emailRedirectTo: `https://clubs.clubcentral.app/${tenantId}/`, // 👈 customize this
      },
    });

    if (error) {
      // Extract clean message
      const cleanMessage =
        error.message?.replace(/^AuthApiError:\s*/, "") || "Unknown error";

      toast.error(`Failed to resend confirmation email - ${cleanMessage}`);
    } else {
      toast.success("Confirmation email has been resent!");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Call backend login endpoint
      const response = await fetch(`${config.BASE_URL}/auth/login`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (data.error?.includes("Email not confirmed")) {
          setShowResend(true);
        }
        setError(data.error || "Invalid login credentials");
        setIsLoading(false);
        return;
      }

      // The one place a sign-in becomes a signed-in app (also used by the
      // magic-link page): saves the token, records who is signed in, and says
      // where this person lands.
      const session = await establishSession(data, { dispatch, tenantId });
      if (!session.ok) {
        setError(SIGN_IN_NOT_SAVED_MESSAGE);
        setIsLoading(false);
        return;
      }
      if (session.destination) {
        navigate(session.destination);
      } else {
        // Default navigation
        navigate(from, { replace: true });
      }
    } catch (err) {
      // Reported: this branch was silent, which is how a full storage made
      // Gymnastics of York's staff "sign in" over and over with no trace.
      reportSignInFailed(err);
      setError("An error occurred during login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAndSetSettings = async () => {
      if (storedSettings) {
        setSettings(JSON.parse(storedSettings));
      } else {
        // Fetch settings from API if not in localStorage.
        // Guarded: this is a bare async effect on the LOGIN page, so an
        // unhandled rejection here is the first thing a signed-out visitor
        // would hit. Losing branding is survivable; the form still works.
        try {
          const response = await getSettings();
          if (response?.success) {
            const settingsData = response?.data;
            safeSetItem("settings", JSON.stringify(settingsData));
            setSettings(settingsData);
          }
        } catch (err) {
          captureError(err, { component: "Login", operation: "fetchSettings" }, "warning");
        }
      }
    };

    fetchAndSetSettings();
  }, [storedSettings]);

  return (
    <div className={`min-h-screen flex flex-col justify-center ${getPageBackground(isDarkMode)}`}>
      <Navbar transparent={true} />
      <div className="px-4 sm:mx-auto sm:w-full sm:max-w-md py-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className={`py-6 px-4 shadow sm:rounded-lg sm:px-10 sm:py-8 ${getCardBackground(isDarkMode)}`}>
            <div className="sm:mx-auto sm:w-full sm:max-w-md pb-10">
              <div
                className="py-6"
                style={{ background: isDarkMode ? '#4b5563' : '#6b7280' }}
              >
                {/* Fixed-height slot prevents a CLS jump when the async
                    settings fetch flips this from the text fallback to
                    the logo image. */}
                <div className="h-16 flex items-center justify-center">
                  {settings?.logo_url ? (
                    <img
                      src={settings?.logo_url}
                      alt={`${settings?.business_name} logo`}
                      className="mx-auto h-16 w-auto"
                      width={200}
                      height={64}
                      fetchPriority="high"
                    />
                  ) : (
                    <h2 className={`text-center text-3xl font-extrabold ${getPrimaryText(isDarkMode)}`}>
                      {settings?.business_name || "Athletics Club"}
                    </h2>
                  )}
                </div>
              </div>
              <h2 className={`mt-6 text-center text-3xl font-extrabold ${getPrimaryText(isDarkMode)}`}>
                Sign in to your account
              </h2>
              <p className={`mt-2 text-center text-sm ${getMutedText(isDarkMode)}`}>
                Or{" "}
                <Link
                  to={`/${tenantId}/register`}
                  className="font-medium text-[var(--cc-link)] hover:opacity-80"
                >
                  create a new account
                </Link>
              </p>
            </div>
            {error && (
              // role="alert" so a failed sign-in is ANNOUNCED, not just drawn.
              // Without it a screen-reader user submits the form and gets
              // silence — the same "no feedback, so try again" failure mode as
              // the blank slot picker. data-testid gives tests a stable hook.
              <div
                role="alert"
                data-testid="error-message"
                className="bg-red-50 border-l-4 border-red-400 p-4"
              >
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {showResend && (
              <button
                type="button"
                onClick={resendConfirmation}
                className="text-blue-600 underline mt-2 mb-4"
              >
                Resend Confirmation Email
              </button>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <Input
                label="Email address"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <div className="relative">
                <Input
                  label="Password"
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center mt-6"
                >
                    {showPassword ? (
                      <svg
                        className={`h-5 w-5 ${getMutedText(isDarkMode)} hover:${getSecondaryText(isDarkMode)}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className={`h-5 w-5 ${getMutedText(isDarkMode)} hover:${getSecondaryText(isDarkMode)}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.275 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>

              {/* No "Remember me" (removed 2026-10-11): it was never read, so
                  ticking or clearing it changed nothing. Every sign-in stays
                  signed in on this device until Log Out, or 7 days without
                  use. */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-3 sm:space-y-0">
                <div className="text-sm">
                  <Link
                    to={`/${tenantId}/forgot-password`}
                    className="font-medium text-[var(--cc-link)] hover:opacity-80"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                loading={isLoading}
                disabled={isLoading}
              >
                Sign in
              </Button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
